import React, { FC, useMemo, useState } from 'react';
import { FormLabel } from 'react-bootstrap';
import { $Formutil, connect, EasyField } from 'react-bootstrap-formutil';
import Modal from 'components/base/Modal';
import { Collapse } from 'components/Transition';
import UI from 'stores/ui';
import classlist from 'utils/classlist';
import { BaseHomeAddressProps } from '../../type';
import ProvinceCascader from './ProvinceCascader';
import ProvincePicker from './ProvincePicker';
import { ProvinceFormFieldsType } from './type';
import './style.scss';

interface ProvinceCityProps {
    $formutil: $Formutil<ProvinceFormFieldsType>;
    placeholder?: string;
    onChange?: (value: string) => void;
    okText?: string;
    dismissText?: string;
    disabled?: boolean;
    separate?: string;
    validMessage?: string;
    label?: string;
    nameOptions?: {
        // name字段前缀
        prefix?: BaseHomeAddressProps['prefix'];
        provinceName?: string;
        cityName?: string;
        districtName?: string;
    };
}

const ProvinceCity: FC<ProvinceCityProps> = ({
    placeholder = __('请选择完整的省市区县'),
    validMessage = __('请选择完整的省市区县'),
    separate = '/',
    nameOptions,
    onChange,
    disabled,
    $formutil,
    label
}) => {
    const [showOption, setShowOption] = useState<boolean>(false);
    const { $params, $errors, $dirts, $setDirts, $setValues } = $formutil;
    const { provinceName, cityName, districtName } = useMemo(() => {
        const { prefix = 'home', provinceName, cityName, districtName } = nameOptions || {};

        return {
            provinceName: provinceName || `${prefix}_state`,
            cityName: cityName || `${prefix}_city`,
            districtName: districtName || `${prefix}_district`
        };
    }, [nameOptions]);

    const handleClickOption = (ev: React.MouseEvent) => {
        ev.stopPropagation();

        const value = getValue();

        if (UI.isMobile) {
            Modal.open({
                component: props => <ProvincePicker {...props} value={value} onChange={onChange} />,
                animation: 'slide',
                className: 'province-picker-modal'
            })
                .result.then(value => {
                    handleChange(value);
                })
                .catch(() => {})
                .finally(() => {
                    $formutil.$setDirts({
                        [provinceName!]: true
                    });
                });
        } else {
            setShowOption(true);
        }
    };

    const isError = () => {
        return $errors[provinceName] || $errors[cityName] || $errors[districtName];
    };

    const closeOption = () => {
        setShowOption(false);

        $setDirts({
            [provinceName]: true
        });
    };

    const handleChange = (value: string[], shouldCloseOption?: boolean) => {
        $setValues({
            [provinceName!]: value[0],
            [cityName]: value[1],
            [districtName!]: value[2]
        });

        if (shouldCloseOption) {
            closeOption();
        }
    };

    const handleProvinceChange = () => {
        if (!$dirts[provinceName]) {
            $setDirts({
                [provinceName]: true
            });
        }
    };

    const getValue = () => {
        return [$params[provinceName], $params[cityName], $params[districtName]].filter(Boolean);
    };

    const isDirty = $dirts[provinceName!];
    const value = getValue();
    const text = value?.join(` ${separate} `);

    return (
        <div className="province-city-root">
            <div
                className={classlist('mb-3', {
                    'has-error': isError() && isDirty
                })}>
                {label && (
                    <div className="form-header">
                        <FormLabel>{label}</FormLabel>
                    </div>
                )}
                <div className="cascader-wrapper">
                    <div
                        className={classlist('form-control form-control-content', {
                            'is-invalid': isError() && isDirty,
                            'text-placeholder': !text,
                            focus: showOption,
                            disabled: disabled
                        })}
                        onClick={disabled ? undefined : handleClickOption}>
                        <span className="value">{text || placeholder}</span>
                    </div>
                    <Collapse unmountOnExit in={showOption && !UI.isMobile} direction="v" timeout={150}>
                        <ProvinceCascader onChange={handleChange} value={value} onCancel={closeOption} />
                    </Collapse>
                </div>
                {isError() && isDirty && <div className="invalid-feedback">{validMessage}</div>}
            </div>
            <EasyField
                type="hidden"
                validMessage={{ required: validMessage }}
                $onFieldChange={handleProvinceChange}
                required
                name={provinceName}
            />
            <EasyField type="hidden" validMessage={{ required: validMessage }} required name={cityName} />
            <EasyField type="hidden" validMessage={{ required: validMessage }} required name={districtName} />
        </div>
    );
};

export default connect(ProvinceCity);
